import React, { Children } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/ui/Footer';
import { motion } from 'framer-motion';
import { Target, Database, Shield, TrendingUp, Users, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.8,
    ease: [0.22, 1, 0.36, 1]
  }
};
const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};
export function StrategyServicesPage() {
  const services = [{
    icon: Target,
    title: 'MDM Strategy & Roadmap',
    description: 'Comprehensive assessment and strategic planning for Master Data Management implementation.',
    features: ['Current state assessment', 'ROI analysis and business case development', 'Technology platform selection', 'Implementation roadmap and timeline']
  }, {
    icon: Database,
    title: 'Data Governance Framework',
    description: 'Establish enterprise-wide data governance policies, procedures, and stewardship programs.',
    features: ['Governance framework design', 'Data stewardship program setup', 'Policy and procedure documentation', 'Ongoing governance support']
  }, {
    icon: Shield,
    title: 'Cloud Data Strategy',
    description: 'Modern cloud-based data architecture and migration strategies for scalable, secure data management.',
    features: ['Cloud platform evaluation', 'Migration planning and execution', 'Security and compliance framework', 'Cost optimization strategies']
  }, {
    icon: TrendingUp,
    title: 'Data Quality & Enrichment',
    description: 'Implement data quality frameworks and enrichment strategies to ensure accurate, complete data.',
    features: ['Data quality assessment', 'Deduplication and matching strategies', 'Third-party data enrichment integration', 'Ongoing data quality monitoring']
  }, {
    icon: Users,
    title: 'Change Management & Training',
    description: 'Ensure successful adoption through comprehensive change management and user training programs.',
    features: ['Stakeholder engagement strategy', 'User training and documentation', 'Change impact assessment', 'Post-implementation support']
  }];
  return <div className="bg-black min-h-screen" data-id="element-317">
      <Header data-id="element-318" />
      <main className="pt-32 pb-24 px-6" data-id="element-319">
        <div className="max-w-7xl mx-auto" data-id="element-320">
          <motion.div className="mb-16 text-center" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-321">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mb-6" data-id="element-322">
              Strategy Services
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed" data-id="element-323">
              Comprehensive data strategy and implementation services designed
              to transform your healthcare data ecosystem.
            </p>
          </motion.div>
          <motion.div className="grid lg:grid-cols-2 gap-8 mb-16" variants={staggerContainer} initial="initial" whileInView="animate" viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-324">
            {services.map((service, index) => <motion.div key={index} variants={fadeInUp} className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-2xl p-8" data-id="element-325">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center mb-6" data-id="element-326">
                  <service.icon className="w-8 h-8 text-white" data-id="element-327" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4" data-id="element-328">
                  {service.title}
                </h3>
                <p className="text-white/70 leading-relaxed mb-6" data-id="element-329">
                  {service.description}
                </p>
                <ul className="space-y-3" data-id="element-330">
                  {service.features.map((feature, idx) => <li key={idx} className="flex items-start gap-2 text-white/70" data-id="element-331">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" data-id="element-332" />
                      <span data-id="element-333">{feature}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </motion.div>
          <motion.div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-2xl p-8 lg:p-12" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-334">
            <div className="max-w-3xl mx-auto text-center" data-id="element-335">
              <h2 className="text-3xl font-semibold text-white mb-6" data-id="element-336">
                Our Approach: Business-First, Implementation-Focused
              </h2>
              <p className="text-lg text-white/80 leading-relaxed mb-8" data-id="element-337">
                Unlike traditional consultancies that leave you with PowerPoint
                decks, we roll up our sleeves and implement end-to-end. Our team
                becomes your team, delivering measurable results while your
                people stay focused on running the business.
              </p>
              <motion.div className="grid md:grid-cols-3 gap-6 mb-8" variants={staggerContainer} initial="initial" whileInView="animate" viewport={{
              once: true
            }} data-id="element-338">
                {[{
                title: 'Discover',
                desc: 'Find the money — quantify the leaks and show hard-dollar impact'
              }, {
                title: 'Design',
                desc: 'Build the blueprint — align executives and define golden records'
              }, {
                title: 'Deploy',
                desc: 'Do the work — hands-on implementation from start to finish'
              }].map((phase, index) => <motion.div key={index} variants={fadeInUp} className="bg-black/30 border border-white/10 rounded-xl p-6" data-id="element-339">
                    <h3 className="text-xl font-semibold text-white mb-3" data-id="element-340">
                      {phase.title}
                    </h3>
                    <p className="text-white/70 text-sm" data-id="element-341">{phase.desc}</p>
                  </motion.div>)}
              </motion.div>
              <motion.div whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} data-id="element-342">
                <Link to="/contact" className="inline-flex items-center justify-center h-12 rounded-xl px-8 text-sm bg-white text-black font-semibold hover:bg-white/90 transition-colors" data-id="element-343">
                  Discuss Your Strategy Needs
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-344" />
    </div>;
}